package com.dotdrives.web.pages

import com.dotdrives.design.DotButtonVariant
import com.dotdrives.design.DotColors
import com.dotdrives.design.DotTone
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotAvatar
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotCard
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotListItem
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import com.dotdrives.web.ui.DotTextField
import react.ChildrenBuilder
import react.FC
import react.Props
import react.PropsWithChildren
import react.create
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import react.useState
import web.cssom.ClassName
import web.html.InputType
import web.html.email

private data class ColorToken(
    val name: String,
    val hex: String,
)

private val colorTokens = listOf(
    ColorToken("PRIMARY", DotColors.PRIMARY),
    ColorToken("PRIMARY_HOVER", DotColors.PRIMARY_HOVER),
    ColorToken("PRIMARY_TINT", DotColors.PRIMARY_TINT),
    ColorToken("PRIMARY_TINT_TEXT", DotColors.PRIMARY_TINT_TEXT),
    ColorToken("INK", DotColors.INK),
    ColorToken("BACKGROUND", DotColors.BACKGROUND),
    ColorToken("BORDER", DotColors.BORDER),
    ColorToken("TEXT_MUTED", DotColors.TEXT_MUTED),
    ColorToken("TEXT_SECONDARY", DotColors.TEXT_SECONDARY),
    ColorToken("SUCCESS", DotColors.SUCCESS),
    ColorToken("SUCCESS_TINT", DotColors.SUCCESS_TINT),
    ColorToken("SUCCESS_TEXT", DotColors.SUCCESS_TEXT),
    ColorToken("INFO", DotColors.INFO),
    ColorToken("INFO_TINT", DotColors.INFO_TINT),
    ColorToken("INFO_TEXT", DotColors.INFO_TEXT),
    ColorToken("DANGER", DotColors.DANGER),
    ColorToken("DANGER_TINT", DotColors.DANGER_TINT),
    ColorToken("AUTOMATION", DotColors.AUTOMATION),
    ColorToken("AUTOMATION_TINT", DotColors.AUTOMATION_TINT),
    ColorToken("WARNING_TEXT", DotColors.WARNING_TEXT),
    ColorToken("WARNING_TINT", DotColors.WARNING_TINT),
)

private external interface GallerySectionProps : PropsWithChildren {
    var eyebrow: String
    var title: String
}

private val GallerySection = FC<GallerySectionProps> { props ->
    section {
        className = ClassName("gallery-section")
        DotCard {
            className = "gallery-card"
            div {
                className = ClassName("gallery-eyebrow dot-meta")
                +props.eyebrow
            }
            h2 {
                className = ClassName("dot-heading dot-h2 gallery-section-title")
                +props.title
            }
            div {
                className = ClassName("gallery-section-content")
                +props.children
            }
        }
    }
}

private fun ChildrenBuilder.colorSwatch(token: ColorToken) {
    val swatchStyle = js("({})")
    swatchStyle.background = token.hex

    div {
        className = ClassName("gallery-swatch")
        div {
            className = ClassName("gallery-swatch-color")
            style = swatchStyle
            ariaHidden = true
        }
        div {
            className = ClassName("gallery-swatch-copy")
            div {
                className = ClassName("dot-label")
                +token.name
            }
            div {
                className = ClassName("dot-meta gallery-muted")
                +token.hex
            }
        }
    }
}

val GalleryPage = FC<Props> {
    val (fieldValue, setFieldValue) = useState("")
    val (clickCount, setClickCount) = useState(0)
    val onDemoClick = { setClickCount(clickCount + 1) }

    div {
        className = ClassName("gallery-page")
        header()
        main {
            className = ClassName("gallery-main")

            GallerySection {
                eyebrow = "FOUNDATIONS"
                title = "Color tokens"
                div {
                    className = ClassName("gallery-token-grid")
                    colorTokens.forEach { colorSwatch(it) }
                }
            }

            GallerySection {
                eyebrow = "ACTIONS"
                title = "Buttons"
                div {
                    className = ClassName("gallery-stack")
                    DotButton {
                        text = "Primary action"
                        onClick = onDemoClick
                        className = "dot-full-width"
                    }
                    DotButton {
                        text = "Secondary action"
                        onClick = onDemoClick
                        variant = DotButtonVariant.Secondary
                        className = "dot-full-width"
                    }
                    DotButton {
                        text = "Tinted action"
                        onClick = onDemoClick
                        variant = DotButtonVariant.Tinted
                        className = "dot-full-width"
                    }
                    DotButton {
                        text = "Disabled"
                        onClick = onDemoClick
                        enabled = false
                        className = "dot-full-width"
                    }
                    DotButton {
                        text = "Saving"
                        onClick = onDemoClick
                        loading = true
                        className = "dot-full-width"
                    }
                    div {
                        className = ClassName("dot-meta gallery-muted")
                        +"Button clicks: $clickCount"
                    }
                }
            }

            GallerySection {
                eyebrow = "INPUT"
                title = "Text fields"
                div {
                    className = ClassName("gallery-stack gallery-stack-lg")
                    DotTextField {
                        value = fieldValue
                        onValueChange = { setFieldValue(it) }
                        label = "Email address"
                        placeholder = "you@example.org"
                        type = InputType.email
                    }
                    DotTextField {
                        value = "Sarah James"
                        onValueChange = {}
                        label = "Steward"
                    }
                    DotTextField {
                        value = "not-an-email"
                        onValueChange = {}
                        label = "Error state"
                        error = "Enter a valid email address."
                        type = InputType.email
                    }
                }
            }

            GallerySection {
                eyebrow = "SURFACES"
                title = "Cards"
                div {
                    className = ClassName("gallery-stack gallery-stack-md")
                    DotCard {
                        h3 {
                            className = ClassName("dot-heading dot-h3")
                            +"Clean Water Fund"
                        }
                        p {
                            className = ClassName("dot-body gallery-copy")
                            +"A quiet white surface with the shared border, radius, and shadow."
                        }
                    }
                    DotCard {
                        onClick = onDemoClick
                        div {
                            className = ClassName("gallery-row")
                            div {
                                div {
                                    className = ClassName("dot-label")
                                    +"Clickable card"
                                }
                                p {
                                    className = ClassName("dot-body gallery-muted")
                                    +"Select to open details"
                                }
                            }
                            span {
                                className = ClassName("dot-heading dot-h3 gallery-arrow")
                                +"→"
                            }
                        }
                    }
                }
            }

            GallerySection {
                eyebrow = "STATUS"
                title = "Pill tones"
                div {
                    className = ClassName("gallery-inline")
                    DotTone.entries.forEach { tone ->
                        DotPill {
                            text = tone.name
                            this.tone = tone
                        }
                    }
                }
            }

            GallerySection {
                eyebrow = "IDENTITY"
                title = "Avatars"
                div {
                    className = ClassName("gallery-inline gallery-align-center")
                    DotAvatar {
                        initials = "SJ"
                        size = 32
                    }
                    DotAvatar {
                        initials = "AM"
                    }
                    DotAvatar {
                        initials = "DD"
                        size = 56
                    }
                }
            }

            GallerySection {
                eyebrow = "DIRECTORY"
                title = "List items"
                div {
                    className = ClassName("gallery-stack")
                    DotListItem {
                        title = "The Miller Family"
                        subtitle = "Individual · Sarah James"
                        leading = DotAvatar.create {
                            initials = "MF"
                        }
                        trailing = DotPill.create {
                            text = "Major giver"
                            tone = DotTone.Automation
                        }
                        onClick = onDemoClick
                    }
                    DotListItem {
                        title = "Riverside Community Church"
                        subtitle = "Organization · Unassigned"
                        leading = DotAvatar.create {
                            initials = "RC"
                        }
                        trailing = span.create {
                            className = ClassName("dot-meta gallery-muted")
                            +"—"
                        }
                    }
                }
            }

            GallerySection {
                eyebrow = "ZERO STATE"
                title = "Empty state"
                DotEmptyState {
                    emoji = "👥"
                    title = "Your givers live here"
                    body = "Add your first giver to start building a complete fundraising history."
                    primaryAction = DotAction("+ Add one giver", onDemoClick)
                    secondaryAction = DotAction("Import CSV", onDemoClick)
                }
            }

            GallerySection {
                eyebrow = "LOADING"
                title = "Skeletons"
                div {
                    className = ClassName("gallery-stack gallery-stack-lg")
                    repeat(3) { index ->
                        div {
                            className = ClassName("gallery-skeleton-row")
                            DotSkeleton {
                                width = "40px"
                                height = "40px"
                                circle = true
                            }
                            div {
                                className = ClassName("gallery-skeleton-copy")
                                DotSkeleton {
                                    width = if (index == 1) "72%" else "88%"
                                    height = "12px"
                                }
                                DotSkeleton {
                                    width = "52%"
                                    height = "8px"
                                }
                            }
                        }
                    }
                }
            }

            GallerySection {
                eyebrow = "FEEDBACK"
                title = "Banner tones"
                div {
                    className = ClassName("gallery-stack")
                    DotTone.entries.forEach { tone ->
                        DotBanner {
                            message = bannerMessage(tone)
                            this.tone = tone
                            onRetry = onDemoClick.takeIf { tone == DotTone.Danger }
                        }
                    }
                }
            }
        }
    }
}

private fun ChildrenBuilder.header() {
    div {
        className = ClassName("gallery-header")
        div {
            className = ClassName("gallery-header-inner")
            div {
                className = ClassName("dot-meta gallery-brand")
                +"DOT DRIVES · REACT"
            }
            h1 {
                className = ClassName("dot-heading dot-h1 gallery-title")
                +"Shared component gallery"
            }
            p {
                className = ClassName("dot-body gallery-header-copy")
                +"The web building blocks for fundraising that moves."
            }
        }
    }
}

private fun bannerMessage(tone: DotTone): String = when (tone) {
    DotTone.Neutral -> "A useful note for this workflow."
    DotTone.Primary -> "Your changes are ready to review."
    DotTone.Success -> "The giver was saved."
    DotTone.Info -> "Import preview is available."
    DotTone.Danger -> "Givers could not be loaded."
    DotTone.Automation -> "Automation created a next step."
    DotTone.Warning -> "This drive has not moved in 14 days."
}
